import type { LegalEntity } from './legal-entity';

/**
 * Поля, из которых формируются готовые текстовые блоки реквизитов.
 * Форматирование намеренно не меняет содержимое полей: телефоны, названия
 * банков и адреса должны оставаться в том виде, в котором они сохранены.
 */
export interface CompanyRequisitesTextInput {
  readonly legalEntity: LegalEntity;
  readonly legalName: string;
  readonly zipCode: string;
  readonly country: string;
  readonly city: string;
  readonly address: string;
  readonly phone: string;
  readonly inn: string | number | bigint;
  readonly okpo?: string | null;
  readonly bankAccount: string;
  readonly bankName: string;
  readonly bankCode: string;
  readonly bankAddress?: string | null;
  readonly directorPosition: string;
  readonly directorPositionGent: string;
  readonly directorName: string;
  readonly directorNameShort: string;
  readonly actingBasis: string;
}

export interface CompanyRequisitesTextBlocks {
  /** Реквизиты в том же порядке и с теми же подписями, что в актах. */
  readonly requisites: string;
  /** Готовая договорная формулировка о представителе и основании. */
  readonly representative: string;
  /** Должность и имя руководителя в именительном падеже. */
  readonly nominative: string;
}

/**
 * Повторяет блок исполнителя из шаблона акта
 * storage/templates/acts/_partials/requisites.hbs.
 */
export function formatCompanyRequisitesText(input: CompanyRequisitesTextInput): string {
  const hasOkpo = Boolean(input.okpo?.trim());
  const lines = [
    input.legalName,
    `Юридический адрес: ${input.zipCode}, ${input.country}, ${input.city}, ${input.address}`,
    `УНП ${String(input.inn)}${hasOkpo ? `    ОКПО ${input.okpo}` : ''}`,
    `Расчетный счет ${input.bankAccount}`,
    `в ${input.bankName}, код ${input.bankCode}`,
  ];

  if (input.bankAddress?.trim()) lines.push(input.bankAddress);
  lines.push(`Тел. ${input.phone}`);

  return lines.join('\n');
}

export function formatCompanyRepresentativeText(input: CompanyRequisitesTextInput): string {
  if (input.legalEntity === 'IP_ZALESSKY') {
    return `${input.legalName} действует на основании ${input.actingBasis}`;
  }

  return `в лице ${input.directorPositionGent} ${input.directorName}, действующего на основании ${input.actingBasis}`;
}

export function formatCompanyDirectorNominativeText(input: CompanyRequisitesTextInput): string {
  return `${input.directorPosition} ${input.directorNameShort}`;
}

export function buildCompanyRequisitesTextBlocks(
  input: CompanyRequisitesTextInput,
): CompanyRequisitesTextBlocks {
  return {
    requisites: formatCompanyRequisitesText(input),
    representative: formatCompanyRepresentativeText(input),
    nominative: formatCompanyDirectorNominativeText(input),
  };
}
